/**
 * Gmail acknowledgement (#3742): when a mailbox email thread is handled in
 * Messages, OS Buddy marks the original read and labelled in the workshop's
 * Gmail. The workshop's Workspace admin grants OS Buddy's service account
 * domain-wide delegation with `gmail.modify`; OS Buddy then acts only as the
 * mailbox saved here.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  useGmailAckSettings,
  useSaveGmailAckSettings,
  useTestGmailAck,
  type GmailFailureCode,
} from '@/hooks/useInboundDocuments';
import { useNotify } from '@/hooks/useNotify';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LABEL_MAX_LENGTH = 100;

const ERROR_KEYS: Record<GmailFailureCode, string> = {
  delegation_missing: 'inboundEmail.gmailAck.error.delegationMissing',
  invalid_mailbox: 'inboundEmail.gmailAck.error.invalidMailbox',
  token_failed: 'inboundEmail.gmailAck.error.tokenFailed',
  forbidden: 'inboundEmail.gmailAck.error.forbidden',
  rate_limited: 'inboundEmail.gmailAck.error.unavailable',
  gmail_unavailable: 'inboundEmail.gmailAck.error.unavailable',
  network: 'inboundEmail.gmailAck.error.unavailable',
  gmail_error: 'inboundEmail.gmailAck.error.other',
};

export function GmailAckSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const query = useGmailAckSettings();
  const save = useSaveGmailAckSettings();
  const test = useTestGmailAck();
  const stored = query.data;
  const defaultLabel = t('inboundEmail.gmailAck.defaultLabel');
  const storedMailbox = stored?.mailbox ?? '';
  const storedLabel = stored?.labelName ?? defaultLabel;
  const storedEnabled = stored?.enabled === true;

  const [enabled, setEnabled] = useState(storedEnabled);
  const [mailbox, setMailbox] = useState(storedMailbox);
  const [labelName, setLabelName] = useState(storedLabel);
  const [testResult, setTestResult] = useState<{ ok: boolean; code: GmailFailureCode | null } | null>(null);

  useEffect(() => {
    setEnabled(storedEnabled);
    setMailbox(storedMailbox);
    setLabelName(storedLabel);
  }, [storedEnabled, storedMailbox, storedLabel]);

  if (!stored) return null;

  const trimmedMailbox = mailbox.trim().toLowerCase();
  const trimmedLabel = labelName.trim();
  const mailboxInvalid = trimmedMailbox !== '' && !EMAIL_RE.test(trimmedMailbox);
  const labelInvalid = trimmedLabel === '' || trimmedLabel.length > LABEL_MAX_LENGTH;
  const complete = trimmedMailbox !== '' && !mailboxInvalid && !labelInvalid;
  const dirty = enabled !== storedEnabled || trimmedMailbox !== storedMailbox || trimmedLabel !== storedLabel;
  const busy = save.isPending || test.isPending;
  const disabled = !canEdit || !stored.available || busy;

  const handleSave = async () => {
    try {
      await save.mutateAsync({ enabled, mailbox: trimmedMailbox, labelName: trimmedLabel });
      notify.success(t('inboundEmail.gmailAck.saved'));
    } catch {
      notify.error(tc('error'), t('inboundEmail.gmailAck.saveFailed'));
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      setTestResult(await test.mutateAsync({ mailbox: trimmedMailbox, labelName: trimmedLabel }));
    } catch {
      notify.error(tc('error'), t('inboundEmail.gmailAck.testFailed'));
    }
  };

  return (
    <Stack spacing={2} data-testid="gmail-ack-settings">
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('inboundEmail.gmailAck.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('inboundEmail.gmailAck.description')}
        </Typography>
      </Box>

      {!stored.available ? (
        <Alert severity="info">{t('inboundEmail.gmailAck.unavailable')}</Alert>
      ) : (
        <Alert severity="info">
          <Typography variant="body2">{t('inboundEmail.gmailAck.delegationInstructions')}</Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            {t('inboundEmail.gmailAck.clientId')}:{' '}
            <Box component="code" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {stored.clientId}
            </Box>
          </Typography>
          <Typography variant="body2" component="div">
            {t('inboundEmail.gmailAck.scope')}:{' '}
            <Box component="code" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {stored.scope}
            </Box>
          </Typography>
        </Alert>
      )}

      {stored.lastError && (
        <Alert severity="error" data-testid="gmail-ack-last-error">
          {t(ERROR_KEYS[stored.lastError.code])}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} disabled={disabled} />
        }
        label={t('inboundEmail.gmailAck.enabledLabel')}
      />

      <TextField
        label={t('inboundEmail.gmailAck.mailboxLabel')}
        type="email"
        value={mailbox}
        onChange={(event) => {
          setMailbox(event.target.value);
          setTestResult(null);
        }}
        error={mailboxInvalid}
        helperText={mailboxInvalid ? t('inboundEmail.gmailAck.invalidMailbox') : t('inboundEmail.gmailAck.mailboxHelp')}
        disabled={disabled}
        fullWidth
      />

      <TextField
        label={t('inboundEmail.gmailAck.labelNameLabel')}
        value={labelName}
        onChange={(event) => {
          setLabelName(event.target.value);
          setTestResult(null);
        }}
        error={labelInvalid}
        helperText={labelInvalid ? t('inboundEmail.gmailAck.invalidLabel') : t('inboundEmail.gmailAck.labelNameHelp')}
        inputProps={{ maxLength: LABEL_MAX_LENGTH }}
        disabled={disabled}
        fullWidth
      />

      {testResult && (
        <Alert severity={testResult.ok ? 'success' : 'error'} data-testid="gmail-ack-test-result">
          {testResult.ok || testResult.code === null
            ? t('inboundEmail.gmailAck.testOk')
            : t(ERROR_KEYS[testResult.code])}
        </Alert>
      )}

      {canEdit && stored.available && (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => { void handleTest(); }} disabled={!complete || busy}>
            {t('inboundEmail.gmailAck.test')}
          </Button>
          <Button
            variant="contained"
            onClick={() => { void handleSave(); }}
            disabled={!dirty || busy || !complete}
          >
            {tc('save')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
